/**
 * ABTestSuggestions
 *
 * Displays AI-suggested A/B tests grouped by creative, with selection options.
 */

import React, { useState } from 'react';
import {
    FlaskConical,
    Lightbulb,
    ArrowRight,
    Type,
    Image,
    Layers,
    Target,
    Zap,
    TrendingUp,
    AlertCircle,
    CheckCircle
} from 'lucide-react';

const TEST_ELEMENT_ICONS = {
    headline: Type,
    cta: Zap,
    visual: Image,
    angle: Target
};

const IMPACT_COLORS = {
    high: { bg: 'var(--success-100)', text: 'var(--success-700)' },
    medium: { bg: 'var(--warning-100)', text: 'var(--warning-700)' },
    low: { bg: 'var(--info-100)', text: 'var(--info-700)' }
};

const EFFORT_LABELS = {
    low: '⚡ Quick',
    medium: '⏱️ Moderate',
    high: '🔧 Involved'
};

/**
 * Group suggestions by the primary brief they target.
 * Each suggestion's primary brief is the first brief in its briefs/briefIndices array.
 */
function groupSuggestionsByCreative(suggestions, briefs) {
    const groups = new Map(); // briefId -> { brief, suggestions[] }

    for (const suggestion of suggestions) {
        const suggestionBriefs = suggestion.briefs || [];
        const briefIndices = suggestion.briefIndices || [];

        // Resolve the primary brief (first one in the suggestion)
        let primaryBrief = null;
        if (suggestionBriefs.length > 0) {
            primaryBrief = briefs.find(b => b.id === suggestionBriefs[0]?.id) || suggestionBriefs[0];
        } else if (briefIndices.length > 0) {
            primaryBrief = briefs[briefIndices[0]];
        }

        if (!primaryBrief) continue;

        const briefId = primaryBrief.id;
        if (!groups.has(briefId)) {
            groups.set(briefId, { brief: primaryBrief, suggestions: [] });
        }
        groups.get(briefId).suggestions.push(suggestion);
    }

    return Array.from(groups.values());
}

const ABTestSuggestions = ({ suggestions, briefs, onConfigureTest }) => {
    // Track which suggestion is selected per creative group (briefId -> suggestion index)
    const [selectedTests, setSelectedTests] = useState({});

    if (!suggestions || suggestions.length === 0) {
        return (
            <div style={{
                textAlign: 'center',
                padding: 'var(--space-12)'
            }}>
                <div style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto var(--space-4)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <FlaskConical size={40} style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
                <h3 style={{ marginBottom: 'var(--space-2)' }}>No A/B Test Suggestions</h3>
                <p style={{
                    color: 'var(--color-text-secondary)',
                    maxWidth: '400px',
                    margin: '0 auto'
                }}>
                    A/B test suggestions will appear here once you have enough content briefs.
                    Continue to the next step or generate more briefs.
                </p>
            </div>
        );
    }

    const groups = groupSuggestionsByCreative(suggestions, briefs);

    const handleSelectTest = (briefId, suggestionIndex) => {
        setSelectedTests(prev => ({
            ...prev,
            [briefId]: prev[briefId] === suggestionIndex ? null : suggestionIndex
        }));
    };

    const handleConfigure = (briefId, suggestion) => {
        onConfigureTest?.(suggestion);
        // Clear selection after configuring
        setSelectedTests(prev => {
            const next = { ...prev };
            delete next[briefId];
            return next;
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                background: 'var(--warning-50)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--warning-200)'
            }}>
                <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--warning-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Lightbulb size={24} style={{ color: 'var(--warning-600)' }} />
                </div>
                <div>
                    <h3 style={{ margin: '0 0 var(--space-1)' }}>
                        A/B Test Opportunities
                    </h3>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        AI has identified testing opportunities for {groups.length} of your approved creatives.
                        Select a test for each creative you want to test, then configure it.
                    </p>
                </div>
            </div>

            {/* Grouped Suggestions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {groups.map(({ brief, suggestions: groupSuggestions }) => {
                    const briefId = brief.id;
                    const isConfigured = briefs.find(b => b.id === briefId)?.abTest?.isAbTest;
                    const selectedIdx = selectedTests[briefId] ?? null;
                    // Auto-use the only suggestion when there's just one
                    const effectiveSuggestion = groupSuggestions.length === 1
                        ? groupSuggestions[0]
                        : (selectedIdx !== null ? groupSuggestions[selectedIdx] : null);

                    return (
                        <CreativeTestGroup
                            key={briefId}
                            brief={brief}
                            suggestions={groupSuggestions}
                            briefs={briefs}
                            isConfigured={isConfigured}
                            selectedIndex={selectedIdx}
                            onSelectTest={(idx) => handleSelectTest(briefId, idx)}
                            onConfigure={() => handleConfigure(briefId, effectiveSuggestion)}
                        />
                    );
                })}
            </div>

            {/* Skip Note */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)'
            }}>
                <AlertCircle size={16} />
                A/B testing is optional. You can skip this step and proceed to scheduling.
            </div>
        </div>
    );
};

// Creative Test Group — shows one creative with its test options or configured results
const CreativeTestGroup = ({ brief, suggestions, briefs, isConfigured, selectedIndex, onSelectTest, onConfigure }) => {
    const briefData = brief.brief || brief;
    const headline = briefData.selectedHeadline || briefData.headline || 'Untitled';
    const thumbnailUrl = brief.generatedThumbnailUrl || brief.generatedImageUrl;

    // Find the configured test details when already set up
    const currentBrief = briefs.find(b => b.id === brief.id);
    const variantBrief = isConfigured ? briefs.find(b => b.id === `${brief.id}_variant`) : null;
    const testConfig = currentBrief?.abTest;

    return (
        <div style={{
            background: 'var(--color-bg-primary)',
            border: isConfigured ? '1px solid var(--success-400)' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden'
        }}>
            {/* Creative Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)'
            }}>
                {/* Thumbnail */}
                {thumbnailUrl && (
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: 'var(--color-bg-tertiary)'
                    }}>
                        <img
                            src={thumbnailUrl}
                            alt={headline}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {headline}
                    </div>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: 'var(--radius-full)',
                            background: brief.pillarColor || 'var(--color-primary)',
                            display: 'inline-block',
                            flexShrink: 0
                        }} />
                        {brief.pillarName} · {brief.funnelStage} · <span style={{ textTransform: 'capitalize' }}>{brief.outputType}</span>
                    </div>
                </div>
                {isConfigured && (
                    <span style={{
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: 'var(--success-100)',
                        color: 'var(--success-700)',
                        borderRadius: 'var(--radius-full)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexShrink: 0
                    }}>
                        <CheckCircle size={10} />
                        CONFIGURED
                    </span>
                )}
                {!isConfigured && suggestions.length > 1 && (
                    <span style={{
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-secondary)',
                        borderRadius: 'var(--radius-full)',
                        flexShrink: 0
                    }}>
                        {suggestions.length} options
                    </span>
                )}
            </div>

            {/* Configured Test Results */}
            {isConfigured && (
                <ConfiguredTestDetails
                    baseBrief={currentBrief}
                    variantBrief={variantBrief}
                    testConfig={testConfig}
                />
            )}

            {/* Test Options (unconfigured) */}
            {!isConfigured && (
                <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)'
                    }}>
                        {suggestions.map((suggestion, idx) => (
                            <TestOptionRow
                                key={idx}
                                suggestion={suggestion}
                                isSelected={selectedIndex === idx}
                                onSelect={() => onSelectTest(idx)}
                                showRadio={suggestions.length > 1}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Footer */}
            {!isConfigured && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 'var(--space-3) var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <button
                        onClick={onConfigure}
                        className="btn btn-primary btn-sm"
                        disabled={suggestions.length > 1 && selectedIndex === null}
                        title={suggestions.length > 1 && selectedIndex === null ? 'Select a test option first' : undefined}
                    >
                        Configure Test
                        <ArrowRight size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

// Shows the A vs B comparison after a test is configured
const ConfiguredTestDetails = ({ baseBrief, variantBrief, testConfig }) => {
    const baseData = baseBrief?.brief || baseBrief;
    const variantData = variantBrief?.brief || variantBrief;

    const baseHeadline = baseData?.selectedHeadline || baseData?.headline || 'Untitled';
    const variantHeadline = variantData?.selectedHeadline || variantData?.headline || 'Variant';

    const baseCaption = baseData?.caption || baseData?.post_copy || '';
    const variantCaption = variantData?.caption || variantData?.post_copy || '';

    const ElementIcon = TEST_ELEMENT_ICONS[testConfig?.testElement] || FlaskConical;

    return (
        <div style={{ padding: 'var(--space-4)' }}>
            {/* Test info */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                <ElementIcon size={14} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    textTransform: 'capitalize'
                }}>
                    {testConfig?.testElement} Test
                </span>
                {testConfig?.hypothesis && (
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        — {testConfig.hypothesis}
                    </span>
                )}
            </div>

            {/* A vs B comparison */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-3)',
                alignItems: 'stretch'
            }}>
                {/* Variant A (Original) */}
                <VariantCard
                    label="A"
                    sublabel="Original"
                    headline={baseHeadline}
                    caption={baseCaption}
                    color="var(--color-primary)"
                />

                {/* VS divider */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'var(--color-text-tertiary)'
                    }}>
                        vs
                    </div>
                </div>

                {/* Variant B (Generated) */}
                {variantData ? (
                    <VariantCard
                        label="B"
                        sublabel="Variant"
                        headline={variantHeadline}
                        caption={variantCaption}
                        color="var(--warning-500)"
                    />
                ) : (
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px dashed var(--color-border)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Variant will be generated during scheduling
                    </div>
                )}
            </div>
        </div>
    );
};

// A or B variant display card
const VariantCard = ({ label, sublabel, headline, caption, color }) => (
    <div style={{
        flex: 1,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden'
    }}>
        {/* Label bar */}
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)'
        }}>
            <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                background: color,
                color: 'white',
                borderRadius: 'var(--radius-sm)',
                fontSize: '11px',
                fontWeight: 600
            }}>
                {label}
            </span>
            <span style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                color: 'var(--color-text-secondary)'
            }}>
                {sublabel}
            </span>
        </div>
        {/* Content */}
        <div style={{ padding: 'var(--space-3)' }}>
            <div style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
                marginBottom: caption ? 'var(--space-2)' : 0
            }}>
                {headline}
            </div>
            {caption && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {caption}
                </div>
            )}
        </div>
    </div>
);

// Individual test option row — selectable when multiple options exist
const TestOptionRow = ({ suggestion, isSelected, onSelect, showRadio }) => {
    const {
        testType,
        testElement,
        hypothesis,
        potentialImpact,
        effort,
        recommendation
    } = suggestion;

    const ElementIcon = TEST_ELEMENT_ICONS[testElement] || FlaskConical;
    const impactColors = IMPACT_COLORS[potentialImpact] || IMPACT_COLORS.medium;

    return (
        <div
            onClick={showRadio ? onSelect : undefined}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: isSelected
                    ? '2px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                background: isSelected ? 'var(--color-primary-alpha, rgba(99, 102, 241, 0.04))' : 'var(--color-bg-primary)',
                cursor: showRadio ? 'pointer' : 'default',
                transition: 'border-color 0.15s, background 0.15s'
            }}
        >
            {/* Radio indicator */}
            {showRadio && (
                <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: 'var(--radius-full)',
                    border: isSelected ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                }}>
                    {isSelected && (
                        <div style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: 'var(--radius-full)',
                            background: 'var(--color-primary)'
                        }} />
                    )}
                </div>
            )}

            {/* Test element icon */}
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-primary-alpha, rgba(99, 102, 241, 0.08))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <ElementIcon size={16} style={{ color: 'var(--color-primary)' }} />
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-1)',
                    flexWrap: 'wrap'
                }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', textTransform: 'capitalize' }}>
                        {testElement} Test
                    </span>
                    <span style={{
                        padding: '1px 6px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: impactColors.bg,
                        color: impactColors.text,
                        borderRadius: 'var(--radius-full)',
                        textTransform: 'uppercase'
                    }}>
                        {potentialImpact} Impact
                    </span>
                    <span style={{
                        fontSize: '10px',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        {testType === 'same_visual_different_text' ? 'Same Visual, Different Text' : 'Completely Different'} · {EFFORT_LABELS[effort] || effort}
                    </span>
                </div>
                <p style={{
                    margin: 0,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.4
                }}>
                    {hypothesis}
                </p>
                {recommendation && (
                    <p style={{
                        margin: 'var(--space-1) 0 0',
                        fontSize: '11px',
                        color: 'var(--info-600)',
                        lineHeight: 1.3
                    }}>
                        {recommendation}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ABTestSuggestions;
